import { Component, OnDestroy, OnInit } from '@angular/core';
import { Params, Router, ActivatedRoute } from '@angular/router';
import { AdminUsersService } from '../services/admin-users.service';
import { FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { RouteHelperService } from '../../core/services/route-helper.service';
import { LocationService } from '../../locations/services/location.service';
import { BaseEditComponent } from '../../core/common/base-component';
import { AuthDataStorageService } from '../../auth/services/auth-data-storage.service';
import swal from 'sweetalert2';
const alertify = require("alertify.js");
import { Title } from '@angular/platform-browser';
@Component({
  selector: 'ch-edit-admin-user',
  templateUrl: './edit-admin-user.component.html',
  styleUrls: ['./edit-admin-user.component.css'],
})
export class EditAdminUserComponent extends BaseEditComponent implements OnInit, OnDestroy {
  private userId: string;
  private params: any;
  public initialPageBreadcrumbName: string = 'Admin Users';
  public editMode: boolean;
  public userData: any = {};
  public userRoles = [];
  public locationRestrictions = [];
  public userName: string;
  public errors: any = {};

  constructor(
    private adminUsersService: AdminUsersService,
    private activatedRoute: ActivatedRoute,
    protected router: Router,
    private routeHelperService: RouteHelperService,
    private locationService: LocationService,
    protected authDataStorageService: AuthDataStorageService,
    private titleService: Title,
  ) {
    super();
  }

  ngOnInit() {
    alertify.logPosition("bottom right");

    this.activatedRoute.queryParamMap
      .map((params: Params) => params.params)
      .subscribe(params => {
        this.params = params;
        this.editMode = this.isEditPage();
        this.userId = this.getCurrentUserId();

        this.locationService.getAllLocations().subscribe(resp => {
          let result = [
            {
              id: null,
              name: 'None',
            },
          ];
          let items = resp.json();
          for (let item of items) {
            if ('string' === typeof item.name && '' !== item.name) {
              result.push(item);
            }
          }

          this.locationRestrictions = result;
        });

        if (this.editMode) {
          this.fetchData(this.userId);
        } else {
          this.titleService.setTitle(`New Admin User | CODE Hospitality CMS`);
        }
      });
  }

  ngOnDestroy() {
    this.titleService.setTitle(`CODE Hospitality CMS`);
  }

  public changePassword() {
    this.openChangePasswordPopup().then(
      result => {
        swal.resetDefaults();
        if (!result.value) {
          return;
        }
        const currentPassword = result.value[0];
        const newPassword = result.value[1];
        const confirmedPassword = result.value[2];
        if (newPassword !== confirmedPassword) {
          swal('Oops...', 'The passwords was not the same!<br>Try again', 'error').then(() => {
            this.changePassword();
          });

          return;
        }
        if (currentPassword === newPassword) {
          swal('Oops...', 'Your new password must be different from the new one!<br>Try again', 'error').then(() => {
            this.changePassword();
          });

          return;
        }

        this.adminUsersService.changePassword(currentPassword, newPassword).subscribe(resp => {
          swal('Congratulations', 'Password successfully changed!', 'success');
        });
      },
      () => {
        swal.resetDefaults();
      }
    );
  }

  private openChangePasswordPopup() {
    swal['setDefaults']({
      input: 'password',
    });

    swal['setDefaults']({
      input: 'password',
      confirmButtonText: 'Next &rarr;',
      showCancelButton: true,
      type: 'question',
      showLoaderOnConfirm: true,
      progressSteps: ['1', '2', '3'],
      inputValidator: value => {
        return new Promise((resolve, reject) => {
          if (value) {
            resolve(null);
          } else {
            resolve('You need to write something!');
          }
        });
      },
    });

    var steps = ['What is your current password?', 'Enter the new password', 'Type the new password again'];

    return swal.queue(steps);
  }

  protected getCurrentUserId() {
    return this.params.id;
  }

  protected isEditPage() {
    return this.routeHelperService.isCurrentPageEdit();
  }

  private fetchData(id: string) {
    this.loading = true;
    this.adminUsersService.getUser(id).subscribe(
      data => {
        this.userData = data.json();
        this.userName = this.userData.name;
        this.titleService.setTitle(`${this.userData.name} | Admin Users | CODE Hospitality CMS`);
        this.loading = false;
        this.loaded = true;
      },
      error => {
        this.loading = false;
      }
    );
  }

  public edit(form) {
    if (!form.valid) {
      for (const i in form.controls) {
        form.controls[i].markAsTouched();
      }

      return;
    }

    this.loading = true;
    const methodName = this.editMode ? 'updateUser' : 'addUser';
    this.adminUsersService[methodName](this.userData).subscribe(
      result => {
        this.cancel();
      },
      error => {
        if (error.errors instanceof Array) {
          for (const item of error.errors instanceof Array ? error.errors : []) {
            if (!form.controls[item.field]) {
              continue;
            }
            form.controls[item.field].setErrors({ [item.field]: item.message });
            this.errors[item.field] = item.message;
          }
        } else {
          if (error.status === 409) {
            alertify.error('Username or email already exists');
          } else {
            alertify.error('There was a problem with the request');
          }
        }
        this.loading = false; // HACK: remove this line when the finally block is fixed
      },
      // FIXME: this is not being called for some reason
      () => {
        this.loading = false;
      }
    );
  }

  public cancel() {
    this.router.navigateByUrl('/admin/admin-users');
  }

  public resetPassword(isDirtyUsername) {
    if (isDirtyUsername) {
      return;
    }

    this.changePassword();
    // this.adminUsersService.forgottenPassword(this.userData.username)
    //   .subscribe(
    //   resp => { alert('Reset password request is successfully sent.'); },
    //   error => { }
    //   );
  }

  public block() {
    const methodName = this.userData.isBlocked ? 'unblockUser' : 'blockUser';
    this.adminUsersService[methodName](this.userId).subscribe(
      resp => {
        this.userData.isBlocked = !this.userData.isBlocked;
      },
      error => { }
    );
  }

  public delete() {
    if (!confirm('Are you sure you want to delete this item ?')) {
      return;
    }

    this.adminUsersService.deleteUser(this.userId).subscribe(
      resp => {
        this.cancel();
      },
      error => { }
    );
  }

  get isLoggedUser() {
    return this.userId === this.authDataStorageService.getLoggedUserId();
  }
}



// WEBPACK FOOTER //
// /Users/thxgg/Projects/Work/code-hospitality-cms/src/app/admin-users/edit-admin-user/edit-admin-user.component.ts